import { resolveMapPoint, type MapPoint } from "@/lib/maps/resolveMapPoint";

const MEMORY_CACHE = new Map<string, MapPoint | null>();
const SESSION_KEY = "br-map-geocode-v2";

function isValidPoint(point: MapPoint | null | undefined): point is MapPoint {
  if (!point) return false;
  const { lat, lng } = point;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

function normalizeCacheKey(query: string): string {
  return query.trim().toLowerCase().replace(/\s+/g, " ");
}

function readSessionCache(key: string): MapPoint | null | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Record<string, MapPoint | null>;
    if (!Object.prototype.hasOwnProperty.call(parsed, key)) return undefined;
    return parsed[key];
  } catch {
    return undefined;
  }
}

function writeSessionCache(key: string, value: MapPoint | null): void {
  if (typeof window === "undefined") return;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, MapPoint | null>) : {};
    parsed[key] = value;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
  } catch {
    // ignore quota / private mode
  }
}

function getCached(query: string): MapPoint | null | undefined {
  const key = normalizeCacheKey(query);
  if (MEMORY_CACHE.has(key)) return MEMORY_CACHE.get(key);
  const fromSession = readSessionCache(key);
  if (fromSession !== undefined) {
    MEMORY_CACHE.set(key, fromSession);
    return fromSession;
  }
  return undefined;
}

function setCached(query: string, value: MapPoint | null): void {
  const key = normalizeCacheKey(query);
  MEMORY_CACHE.set(key, value);
  writeSessionCache(key, value);
}

export type MasterLocationParts = {
  city: string;
  province: string;
  country: string;
  neighborhood: string;
  formattedCity: string;
};

/** Parse city/province/country/neighborhood from a master document (no street fields). */
export function extractMasterLocationParts(
  master: Record<string, unknown> | null | undefined
): MasterLocationParts | null {
  if (!master) return null;

  const neighborhood = String(
    master.neighborhoodName ||
      (master.neighborhood &&
      typeof master.neighborhood === "object" &&
      !Array.isArray(master.neighborhood)
        ? (master.neighborhood as { name?: string }).name
        : "") ||
      ""
  ).trim();

  const cityField = master.city;
  let city = "";
  let province = "";
  let country = "";
  let formattedCity = "";

  if (typeof cityField === "string" && cityField.trim()) {
    formattedCity = cityField.trim();
    const parts = formattedCity.split(",").map((p) => p.trim()).filter(Boolean);
    city = parts[0] || "";
    province = parts[1] || "";
    country = parts[2] || "";
  } else if (cityField && typeof cityField === "object" && !Array.isArray(cityField)) {
    const c = cityField as Record<string, unknown>;
    city = String(c.city || c.cityName || "").trim();
    province = String(c.state || c.stateCode || c.admin1Code || "").trim();
    country = String(c.country || c.countryCode || "").trim();
    formattedCity = String(c.formatted || c.cityName || "").trim();
    if (!formattedCity) {
      formattedCity = [city, province, country].filter(Boolean).join(", ");
    }
  }

  if (!city) {
    const cityName = String(master.cityName || "").trim();
    if (cityName) {
      formattedCity = cityName;
      const parts = cityName.split(",").map((p) => p.trim()).filter(Boolean);
      city = parts[0] || "";
      province = province || parts[1] || "";
      country = country || parts[2] || "";
    }
  }

  if (!province) {
    province = String(master.admin1Code || master.stateCode || "").trim();
  }
  if (!country) {
    country = String(master.countryCode || "").trim();
  }

  if (!city && !formattedCity) return null;

  return {
    city: city || formattedCity,
    province,
    country,
    neighborhood,
    formattedCity: formattedCity || [city, province, country].filter(Boolean).join(", "),
  };
}

export function isTorontoMasterLocation(parts: MasterLocationParts): boolean {
  const blob = `${parts.city} ${parts.formattedCity} ${parts.province} ${parts.country}`.toLowerCase();
  const looksToronto = /\btoronto\b/.test(blob);
  if (!looksToronto) return false;
  const looksOn =
    /\bontario\b/.test(blob) ||
    /\bon\b/.test(blob) ||
    parts.province.toUpperCase() === "ON";
  const looksCa =
    /\bcanada\b/.test(blob) ||
    parts.country.toUpperCase() === "CA" ||
    parts.country.toLowerCase() === "canada";
  return looksOn || looksCa || looksToronto;
}

function buildGeocodeQuery(parts: MasterLocationParts, withNeighborhood: boolean): string {
  const pieces: string[] = [];
  if (withNeighborhood && parts.neighborhood) {
    pieces.push(parts.neighborhood);
  }
  if (parts.city) pieces.push(parts.city);
  if (parts.province) pieces.push(parts.province);
  if (parts.country) pieces.push(parts.country);
  return pieces.join(", ");
}

const SAFE_GEOCODE_TYPES = new Set([
  "neighborhood",
  "sublocality",
  "sublocality_level_1",
  "sublocality_level_2",
  "locality",
  "postal_town",
  "administrative_area_level_3",
  "political",
]);

const UNSAFE_ONLY_TYPES = new Set([
  "street_address",
  "premise",
  "subpremise",
  "route",
  "street_number",
]);

function pickSafePlaceResult(
  results: google.maps.places.PlaceResult[] | null
): MapPoint | null {
  if (!results?.length) return null;

  for (const result of results) {
    const types = result.types || [];
    const hasUnsafe = types.some((t) => UNSAFE_ONLY_TYPES.has(t));
    const hasSafe = types.some((t) => SAFE_GEOCODE_TYPES.has(t));
    // Skip exact street/home-style results unless also tagged as area-level.
    if (hasUnsafe && !hasSafe) continue;

    const loc = result.geometry?.location;
    if (!loc) continue;
    const point = {
      lat: typeof loc.lat === "function" ? loc.lat() : Number(loc.lat),
      lng: typeof loc.lng === "function" ? loc.lng() : Number(loc.lng),
    };
    if (!isValidPoint(point)) continue;
    return point;
  }

  // If Places returned a geometry but types were sparse, still accept first valid
  // non-(0,0) point (city/neighborhood queries typically return area geometry).
  for (const result of results) {
    const types = result.types || [];
    const hasUnsafe = types.some((t) => UNSAFE_ONLY_TYPES.has(t));
    if (hasUnsafe) continue;
    const loc = result.geometry?.location;
    if (!loc) continue;
    const point = {
      lat: typeof loc.lat === "function" ? loc.lat() : Number(loc.lat),
      lng: typeof loc.lng === "function" ? loc.lng() : Number(loc.lng),
    };
    if (!isValidPoint(point)) continue;
    return point;
  }

  return null;
}

async function geocodeAddress(query: string): Promise<MapPoint | null> {
  const trimmed = query.trim();
  if (!trimmed) return null;

  const cached = getCached(trimmed);
  if (cached !== undefined) {
    return isValidPoint(cached) ? cached : null;
  }

  if (typeof window === "undefined" || !window.google?.maps?.places?.PlacesService) {
    return null;
  }

  // Use Places (already loaded for city autocomplete) instead of Geocoder,
  // which requires a separate Geocoding API enablement and was REQUEST_DENIED.
  const attribution = document.createElement("div");
  const service = new window.google.maps.places.PlacesService(attribution);

  const point = await new Promise<MapPoint | null>((resolve) => {
    service.findPlaceFromQuery(
      {
        query: trimmed,
        fields: ["geometry", "name", "types", "formatted_address"],
      },
      (results, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          setCached(trimmed, null);
          resolve(null);
          return;
        }
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          // Do not cache denied/over-quota/errors — allow retry later.
          resolve(null);
          return;
        }
        const safe = pickSafePlaceResult(results);
        if (safe) setCached(trimmed, safe);
        resolve(safe);
      }
    );
  });

  return point;
}

/**
 * Resolve a map pin for a master:
 * 1) Toronto + neighborhood → neighborhood geocode (preferred over city-center coords)
 * 2) resolveMapPoint() stored coords
 * 3) neighborhood geocode (non-Toronto / fallback)
 * 4) city geocode
 */
export async function resolveMasterMapPoint(
  master: Record<string, unknown> | null | undefined
): Promise<MapPoint | null> {
  const parts = extractMasterLocationParts(master);
  if (!parts) {
    return resolveMapPoint(master);
  }

  const hasNeighborhood = Boolean(parts.neighborhood);

  // Toronto masters with a saved neighborhood: prefer neighborhood center.
  if (hasNeighborhood && isTorontoMasterLocation(parts)) {
    const neighborhoodPoint = await geocodeAddress(
      buildGeocodeQuery(parts, true)
    );
    if (neighborhoodPoint) return neighborhoodPoint;
  }

  const stored = resolveMapPoint(master);
  if (stored) return stored;

  if (hasNeighborhood) {
    const neighborhoodPoint = await geocodeAddress(
      buildGeocodeQuery(parts, true)
    );
    if (neighborhoodPoint) return neighborhoodPoint;
  }

  const cityPoint = await geocodeAddress(buildGeocodeQuery(parts, false));
  if (cityPoint) return cityPoint;

  return null;
}
